import { Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import {
  amazonAccessProgress,
  amazonTakedownCapability,
  type BrandAmazonAccess,
} from "@/lib/amazonAccess";

type StepStatus = "done" | "in_progress" | "pending";

type Step = {
  id: string;
  label: string;
  hint?: string;
  status: StepStatus;
  cta?: { label: string; to?: string; onClick?: () => void };
};

export function SetupChecklist({
  brands,
}: {
  brands: BrandAmazonAccess[];
}) {
  const progress = amazonAccessProgress(brands);

  if (brands.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
          Add a brand before connecting Amazon
        </div>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Monitoring and takedown capability are configured separately for each brand.
        </p>
        <Link
          to="/dashboard/brands/new"
          className="mt-4 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          Add a brand
        </Link>
      </div>
    );
  }

  const steps: Step[] = brands.map((brand) => {
    const capability = amazonTakedownCapability(brand);
    const status: StepStatus =
      capability === "brand_registry_ready" ||
      capability === "unverified_no_brand_registry"
        ? "done"
        : capability === "brand_registry_access_pending"
          ? "in_progress"
          : "pending";
    return {
      id: brand.id,
      label:
        capability === "brand_registry_ready"
          ? `${brand.name} is ready for managed takedowns`
          : capability === "unverified_no_brand_registry"
            ? `${brand.name} monitoring is ready`
            : capability === "unknown"
              ? `Confirm Brand Registry status for ${brand.name}`
              : `Connect ${brand.name} to Brand Registry`,
      hint:
        capability === "brand_registry_access_pending"
          ? "Invitation sent — we'll verify the Registered Agent role within 24 hours."
          : capability === "unverified_no_brand_registry"
            ? "Managed takedowns are not yet supported for this brand."
            : capability === "brand_registry_ready"
              ? undefined
              : capability === "unknown"
                ? "Monitoring works without Brand Registry; managed takedowns currently require it."
                : "Invite Reputely as a Registered Agent in Brand Registry.",
      status,
      cta:
        status === "done"
          ? undefined
          : {
              label: status === "in_progress" ? "View status →" : "Start setup →",
              to: `/dashboard/onboarding?brand=${encodeURIComponent(brand.id)}`,
            },
    };
  });

  const pct = Math.round((progress.complete / progress.total) * 100);

  if (progress.complete === progress.total) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950/40">
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-900 dark:text-emerald-200">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600">
            <Check className="h-3 w-3 text-white" />
          </span>
          Amazon setup is complete.
        </div>
        <p className="mt-1 text-sm text-emerald-800 dark:text-emerald-300">
          Monitoring can continue for every brand. Managed takedowns are ready
          for {progress.takedownReady} of {progress.total}.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-900 bg-slate-900 p-6 text-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Set up your account</h2>
        <span className="text-xs text-slate-300">
          {progress.complete} of {progress.total} complete
        </span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
        <div
          className="h-full bg-emerald-400 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="mt-6 space-y-5">
        {steps.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </ul>
    </div>
  );
}

function StepRow({ step }: { step: Step }) {
  const { label, hint, status, cta } = step;
  const isDone = status === "done";
  const isInProgress = status === "in_progress";

  return (
    <li className="flex items-start gap-3">
      <div
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 ${
          isDone
            ? "bg-emerald-500 ring-emerald-500"
            : isInProgress
              ? "bg-blue-500 ring-blue-500"
              : "bg-transparent ring-slate-500"
        }`}
        aria-hidden
      >
        {isDone && <Check className="h-3 w-3 text-white" />}
        {isInProgress && (
          <Loader2 className="h-3 w-3 animate-spin text-white" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={`text-sm font-medium ${
            isDone ? "text-emerald-200" : "text-white"
          }`}
        >
          {label}
        </div>
        {hint && (
          <div className="mt-0.5 text-xs text-slate-300">{hint}</div>
        )}
        {cta && (
          <div className="mt-3">
            {cta.to ? (
              <Link
                to={cta.to}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-100"
              >
                {cta.label}
              </Link>
            ) : (
              <button
                onClick={cta.onClick}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-100"
              >
                {cta.label}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
